import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Search } from 'lucide-react';
import { countries, Country, getDefaultCountry } from '../../data/countries';

interface InternationalPhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  required?: boolean;
}

const InternationalPhoneInput = ({
  value,
  onChange,
  disabled,
  className = '',
  placeholder,
  required = false,
}: InternationalPhoneInputProps) => {
  const [selectedCountry, setSelectedCountry] = useState<Country>(getDefaultCountry());
  const [phoneDigits, setPhoneDigits] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter countries based on search term
  const filteredCountries = countries.filter(
    country =>
      country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.dialCode.includes(searchTerm) ||
      country.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Initialize phone digits from value (extract only the number part)
  useEffect(() => {
    if (value) {
      // Extract only digits from the value, ignoring any country codes
      const digits = value.replace(/\D/g, '');
      setPhoneDigits(digits);
    } else {
      setPhoneDigits('');
    }
  }, []);

  // Handle phone number input - ONLY digits, no country code interference
  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    // Extract only digits from input
    const digits = input.replace(/\D/g, '');

    // Limit to reasonable phone number length
    if (digits.length <= 15) {
      setPhoneDigits(digits);

      // Combine country code + digits for the parent component
      const fullNumber = digits ? `${selectedCountry.dialCode}${digits}` : '';
      onChange(fullNumber);
    }
  };

  // Handle country selection - NO effect on phone number input
  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    setIsDropdownOpen(false);
    setSearchTerm('');

    // Update the full number with new country code but keep same digits
    if (phoneDigits) {
      const fullNumber = `${country.dialCode}${phoneDigits}`;
      onChange(fullNumber);
    }
  };

  // Handle dropdown toggle
  const toggleDropdown = () => {
    if (!disabled) {
      setIsDropdownOpen(!isDropdownOpen);
      setSearchTerm('');
    }
  };

  // Handle search input
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setSearchTerm('');
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsDropdownOpen(false);
      setSearchTerm('');
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="flex relative">
        {/* Country Code Selector - Fixed width, completely separate */}
        <button
          type="button"
          onClick={toggleDropdown}
          disabled={disabled}
          className={`flex items-center justify-center gap-1 px-2 py-3 border border-r-0 border-neutral-300 rounded-l-md bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors flex-none w-14 sm:w-20 ${
            disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          }`}
          aria-label={`Selected country: ${selectedCountry.name}`}
          aria-expanded={isDropdownOpen}
          aria-haspopup="listbox"
        >
          <span
            className={`fi fi-${selectedCountry.code.toLowerCase()} fis rounded-full`}
            style={{
              display: 'inline-block',
              width: '24px',
              height: '24px',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              lineHeight: 0,
            }}
          />
          <ChevronDown
            className={`w-3 h-3 text-neutral-500 transition-transform duration-200 ${
              isDropdownOpen ? 'rotate-180' : ''
            }`}
          />
        </button>

        {/* Phone Number Input - COMPLETELY INDEPENDENT */}
        <div className="flex-1">
          <input
            type="tel"
            value={phoneDigits}
            onChange={handlePhoneNumberChange}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            required={required}
            className={`w-full px-4 py-3 border border-neutral-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm sm:text-base ${
              disabled ? 'bg-neutral-50 cursor-not-allowed' : ''
            }`}
            placeholder={placeholder || ''}
            autoComplete="tel"
          />
        </div>
      </div>

      {/* Country Dropdown */}
      <AnimatePresence>
        {isDropdownOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 right-0 mt-1 bg-white border border-neutral-300 rounded-md shadow-lg z-[99999] max-h-80 overflow-hidden country-dropdown"
            role="listbox"
            aria-label="Select country"
          >
            {/* Search Input */}
            <div className="p-3 border-b border-neutral-200">
              <div className="relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder="Search countries..."
                  className="w-full px-4 py-2 pl-10 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                  autoComplete="off"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-500" />
              </div>
            </div>

            {/* Countries List */}
            <div className="max-h-60 overflow-y-auto">
              {filteredCountries.length > 0 ? (
                filteredCountries.map(country => (
                  <motion.button
                    key={country.code}
                    type="button"
                    onClick={() => handleCountrySelect(country)}
                    className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-neutral-50 focus:bg-neutral-50 focus:outline-none text-left transition-colors ${
                      selectedCountry.code === country.code
                        ? 'bg-red-50 text-red-800'
                        : 'text-neutral-700'
                    }`}
                    role="option"
                    aria-selected={selectedCountry.code === country.code}
                    whileHover={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span
                      className={`fi fi-${country.code.toLowerCase()} fis`}
                      style={{
                        display: 'block',
                        width: '24px',
                        height: '18px',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        lineHeight: 0,
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-medium truncate">{country.name}</span>
                        <span className="text-sm text-neutral-500 ml-2">{country.dialCode}</span>
                      </div>
                    </div>
                  </motion.button>
                ))
              ) : (
                <div className="px-4 py-8 text-center text-neutral-500">
                  <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No countries found</p>
                  <p className="text-sm">Try a different search term</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InternationalPhoneInput;
